import { isArray, isFunction, ERRORS_CHANNEL, trim, isRecord, safeToString, isObject, nonNull, onNonNull, isString, ReplayEmitter, wrapError, expiresInDays, isUUID, strEqualsIgnoreCase } from 'live-connect-common';
import { sha1, md5, sha256 } from 'tiny-hashes';

const DEFAULT_AJAX_TIMEOUT = 0;
const DEFAULT_AJAX_RETRIES = 3;
class PixelSender {
    url;
    timeout;
    calls;
    eventBus;
    retries;
    constructor(opts) {
        this.url = opts.collectorUrl ?? 'https://rp.liadm.com';
        this.timeout = opts.ajaxTimeout ?? DEFAULT_AJAX_TIMEOUT;
        this.calls = opts.callHandler;
        this.eventBus = opts.eventBus;
        this.retries = opts.ajaxRetries ?? DEFAULT_AJAX_RETRIES;
    }
    callBakers(bakersJson) {
        try {
            const bakers = JSON.parse(bakersJson).bakers;
            if (isArray(bakers)) {
                for (let i = 0; i < bakers.length; i++)
                    this.calls.pixelGet(`${bakers[i]}?dtstmp=${Date.now()}`);
            }
        }
        catch (e) {
            this.eventBus.emitErrorWithMessage('CallBakers', `Error while calling bakers with ${bakersJson}`, e);
        }
    }
    sendState(state, endpoint, makeCall, onPreSend) {
        if (state.sendsPixel()) {
            if (onPreSend && isFunction(onPreSend)) {
                onPreSend();
            }
            const query = state.asQuery().add('dtstmp', Date.now(), { prepend: true }).toQueryString();
            const uri = `${this.url}/${endpoint}${query}`;
            makeCall(uri);
        }
    }
    sendAjax(state, opts = {}) {
        this.sendState(state, 'j', uri => {
            const go = (remainingRetries) => {
                // additionally set headers extracted from the state only if the state is not empty
                const headers = state.asHeaders();
                this.calls.ajaxGet(uri, bakersJson => {
                    if (opts.onLoad && isFunction(opts.onLoad))
                        opts.onLoad();
                    this.callBakers(bakersJson);
                }, (e) => {
                    if (remainingRetries <= 0) {
                        this.sendPixel(state, opts);
                        this.eventBus.emitError('AjaxFailed', e);
                    }
                    else {
                        go(remainingRetries - 1);
                    }
                }, this.timeout, headers);
            };
            go(this.retries);
        }, opts.onPreSend);
    }
    sendPixel(state, opts = {}) {
        this.sendState(state, 'p', uri => this.calls.pixelGet(uri, opts.onLoad), opts.onPreSend);
    }
}

const EVENT_BUS_NAMESPACE = '__li__evt_bus';
// reexport for backwards compat
const ERRORS_PREFIX = ERRORS_CHANNEL;
const PIXEL_SENT_PREFIX = 'lips';
const PRELOAD_PIXEL = 'pre_lips';
const PEOPLE_VERIFIED_LS_ENTRY = '_li_duid';
const DEFAULT_IDEX_AJAX_TIMEOUT = 5000;
const DEFAULT_IDEX_URL = 'https://idx.liadm.com/idex';
const DEFAULT_REQUESTED_ATTRIBUTES = []; // legacy behaviour; resolves nonId as unifiedId

var consts = /*#__PURE__*/Object.freeze({
    __proto__: null,
    DEFAULT_IDEX_AJAX_TIMEOUT: DEFAULT_IDEX_AJAX_TIMEOUT,
    DEFAULT_IDEX_URL: DEFAULT_IDEX_URL,
    DEFAULT_REQUESTED_ATTRIBUTES: DEFAULT_REQUESTED_ATTRIBUTES,
    ERRORS_PREFIX: ERRORS_PREFIX,
    EVENT_BUS_NAMESPACE: EVENT_BUS_NAMESPACE,
    PEOPLE_VERIFIED_LS_ENTRY: PEOPLE_VERIFIED_LS_ENTRY,
    PIXEL_SENT_PREFIX: PIXEL_SENT_PREFIX,
    PRELOAD_PIXEL: PRELOAD_PIXEL
});

// btoa() as defined by the HTML and Infra specs, which mostly just references RFC 4648.
function btoa(s) {
    // String conversion as required by Web IDL.
    s = `${s}`;
    // "The btoa() method must throw an "InvalidCharacterError" DOMException if
    // data contains any character whose code point is greater than U+00FF."
    for (let i = 0; i < s.length; i++) {
        if (s.charCodeAt(i) > 255) {
            return null;
        }
    }
    let out = '';
    for (let i = 0; i < s.length; i += 3) {
        const groupsOfSix = [undefined, undefined, undefined, undefined];
        groupsOfSix[0] = s.charCodeAt(i) >> 2;
        groupsOfSix[1] = (s.charCodeAt(i) & 0x03) << 4;
        if (s.length > i + 2) {
            groupsOfSix[1] |= s.charCodeAt(i + 1) >> 4;
            groupsOfSix[2] = (s.charCodeAt(i + 1) & 0x0f) << 2;
            groupsOfSix[2] |= s.charCodeAt(i + 2) >> 6;
            groupsOfSix[3] = s.charCodeAt(i + 2) & 0x3f;
        }
        else if (s.length > i + 1) {
            groupsOfSix[1] |= s.charCodeAt(i + 1) >> 4;
            groupsOfSix[2] = (s.charCodeAt(i + 1) & 0x0f) << 2;
        }
        for (let j = 0; j < groupsOfSix.length; j++) {
            const element = groupsOfSix[j];
            if (typeof element === 'undefined') {
                out += '=';
            }
            else {
                out += btoaLookup(element);
            }
        }
    }
    return out;
}
// Lookup table for btoa(), which converts a six-bit number into the corresponding ASCII character.
const keystr = 'ABCDEFGHIJKLMNOPQRSTUVWXYZabcdefghijklmnopqrstuvwxyz0123456789+/';
function btoaLookup(index) {
    return (index >= 0 && index < 64) ? keystr[index] : undefined;
}

function _safeBtoa(s) {
    const res = btoa(s);
    return res || '';
}
const _base64encodeRegex = /[+/]|=+$/g;
const _base64ToUrlEncodedChars = {
    '+': '-',
    '/': '_'
};
function _replaceBase64Chars(x) {
    return _base64ToUrlEncodedChars[x] || '';
}
function base64UrlEncode(s) {
    let btoa = null;
    // First we escape the string using encodeURIComponent to get the UTF-8 encoding of the characters,
    // then we convert the percent encodings into raw bytes, and finally feed it to btoa() function.
    const utf8Bytes = encodeURIComponent(s).replace(/%([0-9A-F]{2})/g, (match, p1) => String.fromCharCode(parseInt('0x' + p1, 16)));
    try {
        btoa = (window && isFunction(window.btoa)) ? window.btoa : _safeBtoa;
    }
    catch (e) {
        btoa = _safeBtoa;
    }
    return btoa(utf8Bytes).replace(_base64encodeRegex, _replaceBase64Chars);
}

const hashLikeRegex = () => /(\s+)?[a-f0-9]{32,64}(\s+)?/gi;
const lengthToHashType = new Map([[32, 'md5'], [40, 'sha1'], [64, 'sha256']]);
function isHash(hash) {
    const extractedHash = extractHashValue(hash);
    return !!extractedHash && lengthToHashType.has(extractedHash.length);
}
function extractHashValue(s) {
    const result = s.match(hashLikeRegex());
    return result && result.map(trim)[0];
}
function hashEmail(email) {
    const lowerCasedEmail = email.toLowerCase();
    return {
        md5: md5(lowerCasedEmail),
        sha1: sha1(lowerCasedEmail),
        sha256: sha256(lowerCasedEmail)
    };
}
function domainHash(domain, limit = 12) {
    return sha1(domain.replace(/^\./, '')).substring(0, limit);
}

const emailRegex = () => /\S+(@|%40)\S+\.\S+/;
function isEmail(s) {
    return emailRegex().test(s);
}
function containsEmailField(s) {
    return emailRegex().test(s);
}
function extractEmail(s) {
    const result = s.match(emailRegex());
    return result && result.map(trim)[0];
}
function listEmailsInString(s) {
    const result = [];
    // eslint-disable-next-line
    const emailLikeRegex = `([\\w\\d.+-]+(@|%40)[\\w\\d-]+.[\\w\\d.-]+)`;
    const multipleEmailLikeRegex = new RegExp(emailLikeRegex, 'g');
    let current = multipleEmailLikeRegex.exec(s);
    while (current) {
        result.push(trim(current[1]));
        current = multipleEmailLikeRegex.exec(s);
    }
    return result;
}
function replaceEmailsWithHashes(originalString) {
    const emailsInString = listEmailsInString(originalString);
    const hashes = [];
    let convertedString = originalString;
    for (let i = 0; i < emailsInString.length; i++) {
        const email = emailsInString[i];
        const emailHashes = hashEmail(email);
        convertedString = convertedString.replace(email, emailHashes.md5);
        hashes.push(emailHashes);
    }
    return {
        stringWithoutRawEmails: convertedString,
        hashesFromOriginalString: hashes
    };
}

const MASK = '*********';
function replacer(key, value) {
    return (typeof value === 'string' && isEmail(trim(value))) ? MASK : value;
}

function _isNum(v) {
    return isNaN(+v) ? v : +v;
}
function _isNull(v) {
    return v === 'null' || v === 'undefined' ? null : v;
}
function _isBoolean(v) {
    return v === 'false' ? false : (v === 'true' ? true : v);
}
function _convert(v) {
    return _isBoolean(_isNull(_isNum(v)));
}
function _parseParam(params, key) {
    if (key in params) {
        const value = params[key];
        if (isArray(value)) {
            return value.map(v => _convert(decodeValue(v)));
        }
        else {
            return _convert(decodeValue(value));
        }
    }
}
function _allParams(url) {
    let questionMarkIndex, queryParams, historyIndex;
    const obj = {};
    if (!url || (questionMarkIndex = url.indexOf('?')) === -1 || !(queryParams = url.slice(questionMarkIndex + 1))) {
        return obj;
    }
    if ((historyIndex = queryParams.indexOf('#')) !== -1 && !(queryParams = queryParams.slice(0, historyIndex))) {
        return obj;
    }
    queryParams.split('&').forEach(function (raw) {
        if (raw) {
            let key;
            const split = raw.split('=');
            key = split[0];
            const value = split.length === 2 ? split[1] : 'true';
            if (key.slice(-2) === '[]') {
                key = key.slice(0, -2);
            }
            if (key in obj) {
                const previous = obj[key];
                if (isArray(previous)) {
                    previous.push(value);
                }
                else {
                    obj[key] = [previous, value];
                }
            }
            else {
                obj[key] = value;
            }
        }
    });
    return obj;
}
function decodeValue(v) {
    return v.replace(/(%[\dA-F]{2})+/gi, decodeURIComponent);
}
function urlParamsArray(url) {
    const params = _allParams(url);
    const result = [];
    Object.keys(params).forEach((k) => { result.push([k, _parseParam(params, k)]); });
    return result;
}
function getQueryParameter(url, name) {
    const params = _allParams(url);
    return _parseParam(params, name);
}
class ParsedUrl {
    hash;
    host;
    hostname;
    pathname;
    port;
    protocol;
    search;
    constructor(url) {
        // eslint-disable-next-line
        const urlRegExp = /^(https?\:\/\/)?(([^:\/?#]*)(?:\:([0-9]+))?)([\/]{0,1}[^?#]*)(\?[^#]*|)(#.*|)$/;
        const match = url.match(urlRegExp);
        if (match === null) {
            throw new TypeError(`Failed to parse URL: ${url}`);
        }
        else {
            this.protocol = match[1] || '';
            this.host = match[2] || '';
            this.hostname = match[3] || '';
            this.port = match[4] || '';
            this.pathname = match[5] || '';
            this.search = match[6] || '';
            this.hash = match[7] || '';
        }
    }
    toString() {
        return `${this.protocol}${this.host}${this.pathname}${this.search}${this.hash}`;
    }
}

const MAX_ITEMS = 10;
const LIMITING_KEYS = ['items', 'itemids'];
const HASH_BEARERS = ['email', 'emailhash', 'hash', 'hashedemail'];
function extractProvidedAttributes(eventSource) {
    const extraFields = { eventSource };
    // add provided email hashes. Only consider the first one found.
    for (const key of Object.keys(eventSource)) {
        const lowerCased = key.toLowerCase();
        if (HASH_BEARERS.indexOf(lowerCased) > -1) {
            const value = trim(safeToString(eventSource[key]));
            const extractedEmail = extractEmail(value);
            const extractedHash = extractHashValue(value);
            if (extractedEmail) {
                const hashes = hashEmail(decodeValue(extractedEmail));
                extraFields.hashedEmail = [hashes.md5, hashes.sha1, hashes.sha256];
                break;
            }
            else if (extractedHash && isHash(extractedHash)) {
                extraFields.hashedEmail = [extractedHash.toLowerCase()];
                break;
            }
        }
    }
    // add provided user agent
    if (typeof eventSource.userAgent === 'string') {
        extraFields.providedUserAgent = eventSource.userAgent;
    }
    // add provided ip4 address
    if (typeof eventSource.ipv4 === 'string') {
        extraFields.providedIPV4 = eventSource.ipv4;
    }
    // add provided ip6 address
    if (typeof eventSource.ipv6 === 'string') {
        extraFields.providedIPV6 = eventSource.ipv6;
    }
    return extraFields;
}
function limitItems(event) {
    const limitedEvent = {};
    Object.keys(event).forEach(key => {
        const lowerCased = key.toLowerCase();
        const value = event[key];
        if (LIMITING_KEYS.indexOf(lowerCased) > -1 && isArray(value) && value.length > MAX_ITEMS) {
            limitedEvent[key] = value.slice(0, MAX_ITEMS);
        }
        else {
            limitedEvent[key] = value;
        }
    });
    return limitedEvent;
}
function fiddle(event) {
    if (isRecord(event)) {
        const extraAttributes = extractProvidedAttributes(event);
        return {
            ...extraAttributes,
            eventSource: limitItems(event)
        };
    }
    else {
        return {};
    }
}
function mergeObjects(obj1, obj2) {
    const res = {};
    function clean(obj) {
        return isObject(obj) ? obj : {};
    }
    function keys(obj) {
        return Object.keys(obj);
    }
    const first = clean(obj1);
    const second = clean(obj2);
    keys(first).forEach(key => {
        res[key] = first[key];
    });
    keys(second).forEach(key => {
        res[key] = second[key];
    });
    return res;
}

class QueryBuilder {
    tuples;
    constructor(tuples = []) {
        this.tuples = tuples;
    }
    add(key, value, options = {}) {
        const { stripEmpty = true, prepend = false } = options;
        if (key === '') {
            return this;
        }
        else if (stripEmpty && value === '') {
            return this;
        }
        else if (prepend) {
            this.tuples.unshift([key, value]);
            return this;
        }
        else {
            this.tuples.push([key, value]);
            return this;
        }
    }
    addOptional(key, value, options = {}) {
        if (nonNull(value)) {
            return this.add(key, value, options);
        }
        else {
            return this;
        }
    }
    addParamsMap(paramsMap) {
        Object.keys(paramsMap).forEach((key) => {
            const value = paramsMap[key];
            if (nonNull(value)) {
                if (isArray(value)) {
                    value.forEach(entry => this.add(key, entry));
                }
                else {
                    this.add(key, value);
                }
            }
        });
        return this;
    }
    copy() {
        return new QueryBuilder(this.tuples.slice());
    }
    filteredCopy(predicate) {
        return new QueryBuilder(this.tuples.filter(([k, v]) => predicate(k, v)));
    }
    toQueryString() {
        let acc = '';
        this.tuples.forEach(([k, v]) => {
            const operator = acc.length === 0 ? '?' : '&';
            acc = `${acc}${operator}${encodeURIComponent(k)}=${encodeURIComponent(v)}`;
        });
        return acc;
    }
}
// for use with QueryBuilder#addOptionalParam. Null has a special meaning for the idCookie.
function encodeIdCookie(value) {
    return value === null ? '' : value;
}

const UrlCollectionModes = {
    full: 'full',
    noPath: 'no_path'
};

function collectUrl(state) {
    if (state.pageUrl === undefined || state.pageUrl.length === 0) {
        return ['', false, []];
    }
    if (isDefaultBehavior(state)) {
        return [state.pageUrl, false, []];
    }
    const url = new ParsedUrl(state.pageUrl);
    const urlQueryParameters = urlParamsArray(url.search);
    const pathRemoved = isPathRemoved(url, state);
    const blockedParams = paramsToDelete(urlQueryParameters, state);
    if (pathRemoved) {
        url.pathname = '/';
    }
    if (blockedParams.length > 0) {
        url.search = queryStringToKeep(urlQueryParameters, blockedParams);
    }
    return [url.toString(), pathRemoved, blockedParams];
}
function stripQueryAndPath(pageUrl) {
    if (pageUrl.length === 0) {
        return undefined;
    }
    else {
        const url = new ParsedUrl(pageUrl);
        url.pathname = '';
        url.search = '';
        return url.toString();
    }
}
function isPathRemoved(url, conf) {
    return conf.urlCollectionMode === UrlCollectionModes.noPath && url.pathname.length > 1;
}
function paramsToDelete(urlQueryParameters, conf) {
    if (conf.queryParametersFilter === undefined || conf.queryParametersFilter === '') {
        return [];
    }
    const filterRegExp = new RegExp(conf.queryParametersFilter);
    return urlQueryParameters.map(keyValuePair => keyValuePair[0]).filter(param => filterRegExp.test(param));
}
function queryStringToKeep(urlQueryParameters, paramsToDelete) {
    const params = urlQueryParameters
        .filter(param => paramsToDelete.indexOf(param[0]) === -1)
        .map(keyValue => `${keyValue[0]}=${keyValue[1]}`);
    if (params.length > 0) {
        return `?${params.join('&')}`;
    }
    else {
        return '';
    }
}
function isDefaultBehavior(state) {
    return (state.urlCollectionMode === undefined || state.urlCollectionMode === UrlCollectionModes.full) &&
        (state.queryParametersFilter === undefined || state.queryParametersFilter === '');
}

const noOpEvents = ['setemail', 'setemailhash', 'sethashedemail'];
class StateWrapper {
    data;
    constructor(state, eventSource, error, eventBus) {
        const data = StateWrapper.safeFiddle(state, eventSource, eventBus);
        if (error) {
            data.errorDetails = error;
        }
        this.data = data;
    }
    static safeFiddle(state, eventSource, eventBus) {
        try {
            return mergeObjects(state, fiddle(JSON.parse(JSON.stringify(eventSource))));
        }
        catch (e) {
            console.error(e);
            if (eventBus != null) {
                eventBus.emitErrorWithMessage('StateCombineWith', 'Error while extracting event data', e);
            }
            return {};
        }
    }
    static fromEvent(state, event, eventBus) {
        return new StateWrapper(state, event, undefined, eventBus);
    }
    static fromError(state, error, eventBus) {
        return new StateWrapper(state, {}, error, eventBus);
    }
    setHashedEmail(hashedEmail) {
        this.data.hashedEmail = hashedEmail;
    }
    getHashedEmail() {
        return this.data.hashedEmail || [];
    }
    sendsPixel() {
        const source = isObject(this.data.eventSource) ? this.data.eventSource : {};
        const eventKeys = Object.keys(source)
            .filter(objKey => objKey.toLowerCase() === 'eventname' || objKey.toLowerCase() === 'event');
        const eventKey = eventKeys && eventKeys.length >= 1 && eventKeys[0];
        const eventName = eventKey && trim(source[eventKey]);
        return !eventName || noOpEvents.indexOf(eventName.toLowerCase()) === -1;
    }
    asHeaders() {
        return this.data.providedUserAgent ? { 'X-LI-Provided-User-Agent': this.data.providedUserAgent } : {};
    }
    asQuery() {
        const state = this.data;
        // roughly add parameters in order of importance as the url might get truncated
        const builder = new QueryBuilder()
            .addOptional('aid', state.appId)
            .addOptional('did', state.distributorId)
            .addOptional('se', onNonNull(state.eventSource, s => base64UrlEncode(JSON.stringify(s, replacer))))
            .addOptional('duid', state.liveConnectId)
            .addOptional('tv', state.trackerVersion);
        if (nonNull(state.pageUrl)) {
            const [url, isPathRemoved, blockedParams] = collectUrl(state);
            builder
                .add('pu', url)
                .addOptional('pu_rp', isPathRemoved ? '1' : undefined)
                .add('pu_rqp', blockedParams.join(','));
        }
        builder.addOptional('ae', onNonNull(state.errorDetails, ed => base64UrlEncode(JSON.stringify(ed))));
        if (isArray(state.retrievedIdentifiers)) {
            state.retrievedIdentifiers.forEach((i) => builder.add(`ext_${i.name}`, i.value));
        }
        if (isArray(state.hashesFromIdentifiers)) {
            state.hashesFromIdentifiers.forEach((h) => builder.add('scre', `${h.md5},${h.sha1},${h.sha256}`));
        }
        builder
            .addOptional('li_did', state.decisionIds?.join(','))
            .addOptional('e', state.hashedEmail?.join(','))
            .addOptional('us_privacy', state.usPrivacyString)
            .addOptional('wpn', state.wrapperName)
            .addOptional('gdpr', onNonNull(state.gdprApplies, v => v ? '1' : '0'))
            .addOptional('gdpr_consent', state.gdprConsent)
            .addOptional('refr', state.referrer)
            .addOptional('gpp_s', state.gppString)
            .addOptional('gpp_as', state.gppApplicableSections?.join(','))
            .addOptional('cd', state.cookieDomain)
            .addOptional('ic', encodeIdCookie(state.resolvedIdCookie), { stripEmpty: false })
            .addOptional('c', state.contextElements)
            .addOptional('pip', onNonNull(state.providedIPV4, v => base64UrlEncode(v)))
            .addOptional('pip6', onNonNull(state.providedIPV6, v => base64UrlEncode(v)));
        return builder;
    }
}

function loadedDomain() {
    return (document.domain || (document.location && document.location.host)) || (window && window.location && window.location.host) || 'localhost';
}
function getReferrer(win = window) {
    return _safeGet(() => win.top.document.referrer);
}
function getPage(win = window) {
    const ancestorOrigins = _safeGet(() => win.location.ancestorOrigins) || [];
    const windows = [];
    let currentWindow = win;
    while (currentWindow !== top) {
        windows.push(currentWindow);
        currentWindow = currentWindow.parent;
    }
    windows.push(currentWindow);
    let detectedPageUrl;
    for (let i = windows.length - 1; i >= 0 && !detectedPageUrl; i--) {
        detectedPageUrl = _safeGet(() => windows[i].location.href);
        if (i !== 0) {
            if (!detectedPageUrl)
                detectedPageUrl = _safeGet(() => windows[i - 1].document.referrer);
            if (!detectedPageUrl)
                detectedPageUrl = ancestorOrigins[i - 1];
        }
    }
    return detectedPageUrl;
}
function getContextElements(privacyMode, contextSelectors, contextElementsLength) {
    if (privacyMode || !contextSelectors || contextSelectors === '' || !contextElementsLength) {
        return '';
    }
    else {
        const collectedElements = _collectElementsText(contextSelectors, contextElementsLength);
        return base64UrlEncode(collectedElements);
    }
}
function _collectElementsText(contextSelectors, contextElementsLength) {
    const collectedElements = window.document.querySelectorAll(contextSelectors);
    let collectedString = '';
    for (let i = 0; i < collectedElements.length; i++) {
        const nextElement = replaceEmailsWithHashes(collectedElements[i].outerHTML).stringWithoutRawEmails;
        const maybeCollectedString = collectedString + nextElement;
        if (encodedByteCount(maybeCollectedString) <= contextElementsLength)
            collectedString = maybeCollectedString;
        else
            return collectedString;
    }
    return collectedString;
}
function encodedByteCount(s) {
    // From: https://stackoverflow.com/questions/2219526/how-many-bytes-in-a-javascript-string
    return Math.ceil(new Blob([s]).size * 4 / 3.0);
}
function _safeGet(getter) {
    try {
        return getter();
    }
    catch (e) {
        return undefined;
    }
}

const enrichPage = state => ({
    ...state,
    pageUrl: getPage(),
    referrer: getReferrer(),
    contextElements: getContextElements(state.privacyMode, state.contextSelectors, state.contextElementsLength)
});

function enrichIdentifiers$1(storageHandler, eventBus) {
    return state => {
        try {
            return { ...state, ...getIdentifiers(parseIdentifiersToResolve(state.identifiersToResolve), storageHandler) };
        }
        catch (e) {
            eventBus.emitError('IdentifiersEnricher', e);
            return { ...state, retrievedIdentifiers: [], hashesFromIdentifiers: [] };
        }
    };
}
function parseIdentifiersToResolve(identifiersToResolve) {
    let cookieNames = [];
    if (identifiersToResolve) {
        if (isArray(identifiersToResolve)) {
            cookieNames = identifiersToResolve;
        }
        else if (isString(identifiersToResolve)) {
            cookieNames = identifiersToResolve.split(',');
        }
    }
    for (let i = 0; i < cookieNames.length; i++) {
        cookieNames[i] = cookieNames[i].trim();
    }
    return cookieNames;
}
function getIdentifiers(cookieNames, storageHandler) {
    const identifiers = [];
    let hashes = [];
    for (let i = 0; i < cookieNames.length; i++) {
        const identifierName = cookieNames[i];
        const identifierValue = storageHandler.getCookie(identifierName) || storageHandler.getDataFromLocalStorage(identifierName);
        if (identifierValue) {
            const cookieAndHashes = replaceEmailsWithHashes(safeToString(identifierValue));
            identifiers.push({
                name: identifierName,
                value: cookieAndHashes.stringWithoutRawEmails
            });
            hashes = hashes.concat(cookieAndHashes.hashesFromOriginalString);
        }
    }
    return {
        retrievedIdentifiers: identifiers,
        hashesFromIdentifiers: deduplicateHashes(hashes)
    };
}
function deduplicateHashes(hashes) {
    const seen = new Set();
    const result = [];
    for (let i = 0; i < hashes.length; i++) {
        if (!seen.has(hashes[i].md5)) {
            result.push(hashes[i]);
            seen.add(hashes[i].md5);
        }
    }
    return result;
}

const enrichPrivacyMode = state => ({ ...state, privacyMode: !!state.gdprApplies || (state.gppApplicableSections ?? []).indexOf(2) > -1 });

function removeInvalidPairs(config, eventBus) {
    if (config && config.appId && config.distributorId) {
        const distributorId = config.distributorId;
        delete config.distributorId;
        eventBus.emitError('AppIdAndDistributorIdPresent', new Error(`Event contains both appId: ${config.appId} and distributorId: ${distributorId}. Ignoring distributorId`));
    }
    return config;
}

const ID_COOKIE_ATTR = 'idCookie';
class IdentityResolver {
    eventBus;
    calls;
    idexConfig;
    externalIds;
    source;
    publisherId;
    url;
    timeout;
    extraAttributes;
    requestedAttributes;
    // Be careful, this object is mutable. In cases where temporary changes are needed
    // - e.g. adding parameters that are only valid for one single call - ensure to copy it
    query;
    privacyMode;
    resolvedIdCookie;
    generateIdCookie;
    peopleVerifiedId;
    pageUrl;
    constructor(config, calls, eventBus) {
        const nonNullConfig = config || { identityResolutionConfig: {} };
        this.eventBus = eventBus;
        this.calls = calls;
        this.idexConfig = nonNullConfig.identityResolutionConfig || {};
        this.extraAttributes = this.idexConfig.extraAttributes || {};
        this.externalIds = nonNullConfig.retrievedIdentifiers || [];
        this.source = this.idexConfig.source || 'unknown';
        this.publisherId = this.idexConfig.publisherId || 'any';
        this.url = this.idexConfig.url || DEFAULT_IDEX_URL;
        this.timeout = this.idexConfig.ajaxTimeout || nonNullConfig.ajaxTimeout || DEFAULT_IDEX_AJAX_TIMEOUT;
        this.requestedAttributes = this.idexConfig.requestedAttributes || DEFAULT_REQUESTED_ATTRIBUTES;
        this.privacyMode = nonNullConfig.privacyMode ?? false;
        this.resolvedIdCookie = nonNullConfig.resolvedIdCookie;
        this.generateIdCookie = this.idexConfig.idCookieMode === 'generated';
        this.peopleVerifiedId = nonNullConfig.peopleVerifiedId;
        this.pageUrl = nonNullConfig.pageUrl;
        this.query = new QueryBuilder()
            .addOptional('duid', nonNullConfig.peopleVerifiedId)
            .addOptional('us_privacy', nonNullConfig.usPrivacyString)
            .addOptional('gdpr', onNonNull(nonNullConfig.gdprApplies, v => v ? 1 : 0))
            .addOptional('gdpr_consent', nonNullConfig.gdprConsent)
            .addOptional('did', nonNullConfig.distributorId)
            .addOptional('gpp_s', nonNullConfig.gppString)
            .addOptional('gpp_as', nonNullConfig.gppApplicableSections?.join(','))
            .addOptional('cd', nonNullConfig.cookieDomain)
            .addOptional('ic', encodeIdCookie(nonNullConfig.resolvedIdCookie), { stripEmpty: false })
            .addOptional('pu', onNonNull(nonNullConfig.pageUrl, stripQueryAndPath))
            .addOptional('pip', onNonNull(this.extraAttributes.ipv4, v => base64UrlEncode(v)))
            .addOptional('pip6', onNonNull(this.extraAttributes.ipv6, v => base64UrlEncode(v)));
        this.externalIds.forEach(retrievedIdentifier => {
            this.query.add(retrievedIdentifier.name, retrievedIdentifier.value);
        });
        this.requestedAttributes.forEach(requestedAttribute => {
            this.query.add('resolve', requestedAttribute);
        });
    }
    attributeResolutionAllowed(attribute) {
        if (attribute === 'uid2') {
            return !this.privacyMode;
        }
        else if (attribute === ID_COOKIE_ATTR) {
            // cannot be resolved server-side
            return false;
        }
        else {
            return true;
        }
    }
    filterParams(query) {
        return query.filteredCopy((key, value) => {
            if (key === 'resolve') {
                if (isString(value)) {
                    return this.attributeResolutionAllowed(value);
                }
                else {
                    return false;
                }
            }
            else {
                return true;
            }
        });
    }
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    enrichExtraIdentifiers(response, params) {
        const requestedAttributes = params.tuples.filter(([key]) => key === 'resolve').map(([, value]) => value);
        function requested(attribute) {
            return requestedAttributes.indexOf(attribute) > -1;
        }
        const result = { ...response };
        if (requested(ID_COOKIE_ATTR)) {
            if (this.generateIdCookie && this.peopleVerifiedId) {
                result[ID_COOKIE_ATTR] = this.peopleVerifiedId;
            }
            else if (this.resolvedIdCookie) {
                result[ID_COOKIE_ATTR] = this.resolvedIdCookie;
            }
        }
        return result;
    }
    responseReceived(successCallback, params) {
        return (responseText, response) => {
            // eslint-disable-next-line @typescript-eslint/no-explicit-any
            let responseObj = {};
            if (responseText) {
                try {
                    const responseJson = JSON.parse(responseText);
                    if (isObject(responseJson)) {
                        responseObj = responseJson;
                    }
                }
                catch (ex) {
                    console.error('Error parsing response', ex);
                    this.eventBus.emitError('IdentityResolverParser', ex);
                }
            }
            const expiresAt = responseExpires(response);
            successCallback(this.enrichExtraIdentifiers(responseObj, params), { expiresAt });
        };
    }
    buildUrl(query) {
        return `${this.url}/${this.source}/${this.publisherId}${this.filterParams(query).toQueryString()}`;
    }
    getUrl(additionalParams) {
        const params = this.query.copy().addParamsMap(additionalParams ?? {});
        return this.buildUrl(params);
    }
    resolve(successCallback, errorCallback, additionalParams) {
        try {
            const params = this.query.copy().addParamsMap(additionalParams ?? {});
            this.calls.ajaxGet(this.buildUrl(params), this.responseReceived(successCallback, params), errorCallback, this.timeout);
        }
        catch (e) {
            console.error('IdentityResolve', e);
            if (errorCallback && isFunction(errorCallback)) {
                errorCallback(e);
            }
            if (this.eventBus) {
                this.eventBus.emitError('IdentityResolve', e);
            }
        }
    }
}
function responseExpires(response) {
    if (isObject(response) && 'getResponseHeader' in response && isFunction(response.getResponseHeader)) {
        const expiresHeader = response.getResponseHeader('expires');
        if (expiresHeader) {
            return new Date(expiresHeader);
        }
    }
}

// @ts-nocheck
function initBus(size) {
    if (typeof size === 'number' && size >= 0) {
        return new ReplayEmitter(size);
    }
    else {
        return new ReplayEmitter(5);
    }
}
function extendBusIfNeeded(bus) {
    if (isFunction(bus.emitErrorWithMessage) && isFunction(bus.emitError)) {
        return;
    }
    bus.emitErrorWithMessage = function (name, message, e = {}) {
        const wrappedError = wrapError(name, message, e);
        return bus.emit(ERRORS_CHANNEL, wrappedError);
    };
    bus.emitError = function (name, exception) {
        return bus.emitErrorWithMessage(name, exception.message, exception);
    };
}
function LocalEventBus(size = 5) {
    return initBus(size);
}
function GlobalEventBus(name, size, errorCallback) {
    try {
        if (!window) {
            errorCallback(new Error('Bus can only be attached to the window, which is not present'));
        }
        if (window && !window[name]) {
            window[name] = initBus(size);
        }
        extendBusIfNeeded(window[name]);
        return window[name];
    }
    catch (e) {
        console.error('events.bus.init', e);
        errorCallback(e);
    }
}
function getAvailableBus(name) {
    const eventBus = window[name].eventBus || window[EVENT_BUS_NAMESPACE];
    extendBusIfNeeded(eventBus);
    return eventBus;
}

var eventBus = /*#__PURE__*/Object.freeze({
    __proto__: null,
    GlobalEventBus: GlobalEventBus,
    LocalEventBus: LocalEventBus,
    getAvailableBus: getAvailableBus
});

const TLD_CACHE_KEY = '_li_dcdm_c';
function determineHighestWritableDomain(storageHandler) {
    const cachedDomain = storageHandler.getCookie(TLD_CACHE_KEY);
    if (cachedDomain) {
        return cachedDomain;
    }
    const domain = loadedDomain();
    const arr = domain.split('.');
    for (let i = arr.length; i > 0; i--) {
        const newD = `.${arr.slice(i - 1, arr.length).join('.')}`;
        storageHandler.setCookie(TLD_CACHE_KEY, newD, undefined, 'Lax', newD);
        if (storageHandler.getCookie(TLD_CACHE_KEY)) {
            return newD;
        }
    }
    return `.${domain}`;
}

function enrichDomain(storageHandler) {
    return state => ({ ...state, cookieDomain: determineHighestWritableDomain(storageHandler) });
}

const StorageStrategies = {
    cookie: 'cookie',
    localStorage: 'ls',
    none: 'none', // read-only
    disabled: 'disabled' // completely disabled
};

const enrichStorageStrategy = state => {
    const storageStrategy = state.privacyMode ? StorageStrategies.disabled : (state.storageStrategy || StorageStrategies.cookie);
    return { ...state, storageStrategy };
};

const DEFAULT_DECISION_ID_COOKIE_EXPIRES = expiresInDays(30);
const DECISION_ID_QUERY_PARAM_NAME = 'li_did';
const DECISION_ID_COOKIE_NAMESPACE = 'lidids.';
const _onlyUnique = (value, index, self) => self.indexOf(value) === index;
const _nonEmpty = (value) => value && trim(value).length > 0;
function enrichDecisionIds(storageHandler, eventBus) {
    return state => {
        function addDecisionId(key) {
            if (key) {
                storageHandler.setCookie(`${DECISION_ID_COOKIE_NAMESPACE}${key}`, key, DEFAULT_DECISION_ID_COOKIE_EXPIRES, 'Lax', state.cookieDomain);
            }
        }
        function orElseEmpty(errorDescription, f) {
            try {
                return f();
            }
            catch (e) {
                eventBus.emitErrorWithMessage('DecisionsResolve', errorDescription, e);
                return [];
            }
        }
        const freshDecisions = orElseEmpty('Error while extracting new decision ids', () => {
            const extractedFreshDecisions = [].concat((state.pageUrl && getQueryParameter(state.pageUrl, DECISION_ID_QUERY_PARAM_NAME)) || []);
            return extractedFreshDecisions
                .map(trim)
                .filter(_nonEmpty)
                .filter(isUUID)
                .filter(_onlyUnique);
        });
        const storedDecisions = orElseEmpty('Error while retrieving stored decision ids', () => {
            const extractedStoredDecisions = storageHandler.findSimilarCookies(DECISION_ID_COOKIE_NAMESPACE);
            return extractedStoredDecisions
                .map(trim)
                .filter(_nonEmpty)
                .filter(isUUID);
        });
        freshDecisions.forEach(decision => {
            try {
                addDecisionId(decision);
            }
            catch (e) {
                eventBus.emitErrorWithMessage('DecisionsResolve', 'Error while storing new decision id', e);
            }
        });
        return { ...state, decisionIds: freshDecisions.concat(storedDecisions).filter(_onlyUnique) };
    };
}

// Borrowed from https://github.com/Kiosked/ulid
const ENCODING = '0123456789ABCDEFGHJKMNPQRSTVWXYZ';
const ENCODING_LEN = ENCODING.length;
const TIME_MAX = Math.pow(2, 48) - 1;
const TIME_LEN = 10;
const RANDOM_LEN = 16;
const prng = detectPrng();
function createError(message) {
    const err = new Error(message);
    err.source = 'Ulid';
    return err;
}
function detectPrng() {
    const root = typeof window !== 'undefined' ? window : null;
    const browserCrypto = root && (root.crypto || root.msCrypto);
    if (browserCrypto) {
        return () => {
            const buffer = new Uint8Array(1);
            browserCrypto.getRandomValues(buffer);
            return buffer[0] / 0xff;
        };
    }
    return () => Math.random();
}
function encodeTime(now, len) {
    if (now > TIME_MAX) {
        throw createError('cannot encode time greater than ' + TIME_MAX);
    }
    let mod;
    let str = '';
    for (; len > 0; len--) {
        mod = now % ENCODING_LEN;
        str = ENCODING.charAt(mod) + str;
        now = (now - mod) / ENCODING_LEN;
    }
    return str;
}
function encodeRandom(len) {
    let str = '';
    for (; len > 0; len--) {
        str = randomChar() + str;
    }
    return str;
}
function randomChar() {
    let rand = Math.floor(prng() * ENCODING_LEN);
    if (rand === ENCODING_LEN) {
        rand = ENCODING_LEN - 1;
    }
    return ENCODING.charAt(rand);
}
// the factory to generate unique identifier based on time and current pseudorandom number
function ulid() {
    return encodeTime(Date.now(), TIME_LEN) + encodeRandom(RANDOM_LEN);
}

const NEXT_GEN_FP_NAME = '_lc2_fpi';
function enrichLiveConnectId(cache, storageHandler) {
    return state => {
        let liveConnectIdentifier;
        // reading the value will also repair any broken records
        const oldValue = cache.get(NEXT_GEN_FP_NAME);
        if (oldValue) {
            liveConnectIdentifier = oldValue.data;
        }
        else {
            const legacyValue = storageHandler.getCookie(NEXT_GEN_FP_NAME);
            if (legacyValue) {
                // backwards compatibility case. We have a cookie but no cache metadata.
                // We might overwrite a http cookie here. But this will be fixed with the next http request to cff.
                cache.set(NEXT_GEN_FP_NAME, legacyValue);
                liveConnectIdentifier = legacyValue;
            }
            else {
                const newValue = `${domainHash(state.cookieDomain)}--${ulid()}`.toLocaleLowerCase();
                // will also set cookie. It will also later be extended by cookie bouncing in the cff.
                cache.set(NEXT_GEN_FP_NAME, newValue);
                // handle case when all storage backends are disabled
                liveConnectIdentifier = cache.get(NEXT_GEN_FP_NAME)?.data;
            }
        }
        if (liveConnectIdentifier) {
            // TODO: should we expose this to users even if the write failed?
            storageHandler.setDataInLocalStorage(PEOPLE_VERIFIED_LS_ENTRY, liveConnectIdentifier);
        }
        return {
            ...state,
            liveConnectId: liveConnectIdentifier,
            peopleVerifiedId: liveConnectIdentifier
        };
    };
}

const MAX_ERROR_FIELD_LENGTH = 120;
function _asInt(field) {
    try {
        const intValue = field * 1;
        return isNaN(intValue) ? undefined : intValue;
    }
    catch {
    }
}
function _truncate(value) {
    try {
        if (isString(value) && value.length && value.length > MAX_ERROR_FIELD_LENGTH) {
            return `${value.substr(0, MAX_ERROR_FIELD_LENGTH)}...`;
        }
        else {
            return `${value}`;
        }
    }
    catch {
    }
}
function asErrorDetails(e) {
    if (isRecord(e)) {
        return {
            message: _truncate(e.message) || '',
            name: _truncate(e.name) || '',
            stackTrace: _truncate(e.stack),
            lineNumber: _asInt(e.lineNumber),
            columnNumber: _asInt(e.columnNumber),
            fileName: _truncate(e.fileName)
        };
    }
    else {
        return {
            message: 'Unknown message',
            name: 'Unknown name'
        };
    }
}
function register(state, sender, eventBus) {
    try {
        eventBus.on(ERRORS_CHANNEL, (error) => {
            const wrapped = StateWrapper.fromError(state, asErrorDetails(error), eventBus);
            sender.sendPixel(wrapped);
        });
    }
    catch (e) {
        console.error('handlers.error.register', e);
    }
}

const noop$1 = () => undefined;
class WrappingContext {
    obj;
    name;
    errors;
    eventBus;
    constructor(obj, name, eventBus) {
        this.obj = obj;
        this.name = name;
        this.errors = [];
        this.eventBus = eventBus;
    }
    wrap(functionName) {
        if (isObject(this.obj)) {
            const member = this.obj[functionName];
            if (isFunction(member)) {
                return (...args) => {
                    try {
                        // eslint-disable-next-line @typescript-eslint/no-explicit-any
                        return member.call(this.obj, ...args);
                    }
                    catch (e) {
                        this.eventBus.emitErrorWithMessage(this.name, `Failed calling ${functionName}`, e);
                    }
                };
            }
        }
        this.errors.push(functionName);
        return noop$1;
    }
    reportErrors() {
        if (this.errors.length > 0) {
            this.eventBus.emitErrorWithMessage(this.name, `The functions '${JSON.stringify(this.errors)}' were not provided`);
        }
    }
}

const noop = () => undefined;
function wrapRead(wrapper, storageStrategy, functionName) {
    return strEqualsIgnoreCase(storageStrategy, StorageStrategies.disabled) ? noop : wrapper.wrap(functionName);
}
function wrapWrite(wrapper, storageStrategy, functionName) {
    return strEqualsIgnoreCase(storageStrategy, StorageStrategies.none) ? noop : wrapRead(wrapper, storageStrategy, functionName);
}
class WrappedReadOnlyStorageHandler {
    minimalFunctions;
    constructor(storageStrategy, wrapper) {
        this.minimalFunctions = {
            getCookie: wrapRead(wrapper, storageStrategy, 'getCookie'),
            getDataFromLocalStorage: wrapRead(wrapper, storageStrategy, 'getDataFromLocalStorage'),
            localStorageIsEnabled: wrapWrite(wrapper, storageStrategy, 'localStorageIsEnabled')
        };
    }
    static make(storageStrategy, externalStorageHandler, eventBus) {
        const wrapper = new WrappingContext(externalStorageHandler, 'ReadOnlyStorageHandler', eventBus);
        const handler = new WrappedReadOnlyStorageHandler(storageStrategy, wrapper);
        wrapper.reportErrors();
        return handler;
    }
    localStorageIsEnabled() {
        return !!this.minimalFunctions.localStorageIsEnabled();
    }
    getCookie(key) {
        return this.minimalFunctions.getCookie(key) || null;
    }
    getDataFromLocalStorage(key) {
        return this.minimalFunctions.getDataFromLocalStorage(key) || null;
    }
}
class WrappedStorageHandler extends WrappedReadOnlyStorageHandler {
    storageStrategy;
    functions;
    constructor(storageStrategy, wrapper) {
        super(storageStrategy, wrapper);
        this.storageStrategy = storageStrategy;
        this.functions = {
            setCookie: wrapWrite(wrapper, storageStrategy, 'setCookie'),
            removeDataFromLocalStorage: wrapWrite(wrapper, storageStrategy, 'removeDataFromLocalStorage'),
            setDataInLocalStorage: wrapWrite(wrapper, storageStrategy, 'setDataInLocalStorage'),
            findSimilarCookies: wrapRead(wrapper, storageStrategy, 'findSimilarCookies')
        };
    }
    static make(storageStrategy, externalStorageHandler, eventBus) {
        const wrapper = new WrappingContext(externalStorageHandler, 'StorageHandler', eventBus);
        const handler = new WrappedStorageHandler(storageStrategy, wrapper);
        wrapper.reportErrors();
        return handler;
    }
    setCookie(key, value, expires, sameSite, domain) {
        this.functions.setCookie(key, value, expires, sameSite, domain);
    }
    setDataInLocalStorage(key, value) {
        this.functions.setDataInLocalStorage(key, value);
    }
    removeDataFromLocalStorage(key) {
        this.functions.removeDataFromLocalStorage(key);
    }
    findSimilarCookies(substring) {
        return this.functions.findSimilarCookies(substring) || [];
    }
}

const DEFAULT_COOKIE_EXPIRATION_DAYS = 730;
class ParseError extends Error {
    constructor(message) {
        super(message);
        this.name = 'ParseError';
    }
}
class StorageHandlerBackedCache {
    handler;
    cookieDomain;
    eventBus;
    constructor(opts) {
        this.handler = opts.storageHandler;
        this.cookieDomain = opts.cookieDomain;
        this.eventBus = opts.eventBus;
    }
    deleteCookie(key) {
        this.handler.setCookie(key, '', new Date(0), 'Lax', this.cookieDomain);
    }
    // layout: { w: writtenAt in millis, e? : expiresAt in millis }
    parseMetaRecord(serialized) {
        const meta = JSON.parse(serialized);
        if (!isObject(meta)) {
            throw new ParseError('Meta record is not an object');
        }
        let expiresAt;
        if ('e' in meta) {
            // eslint-disable-next-line @typescript-eslint/no-explicit-any
            expiresAt = new Date(meta.e);
            if (isNaN(expiresAt.getTime())) {
                throw new ParseError('Invalid expiresAt');
            }
        }
        if (!('w' in meta)) {
            throw new ParseError('Missing writtenAt');
        }
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        const writtenAt = new Date(meta.w);
        if (isNaN(writtenAt.getTime())) {
            throw new ParseError('Invalid writtenAt');
        }
        return { expiresAt, writtenAt };
    }
    serializeMetaRecord(meta) {
        let expiresAtMillis;
        if (meta.expiresAt) {
            expiresAtMillis = meta.expiresAt.getTime();
        }
        return JSON.stringify({ w: meta.writtenAt.getTime(), e: expiresAtMillis });
    }
    getCookieRecord(key, metaRecordKey) {
        const metaRecord = this.handler.getCookie(metaRecordKey);
        if (!metaRecord || metaRecord.length === 0) {
            return null;
        }
        let meta;
        try {
            meta = this.parseMetaRecord(metaRecord);
        }
        catch (e) {
            this.eventBus.emitErrorWithMessage('Cache', 'Failed reading meta from cookies', e);
            // delete this so we don't keep trying to read it
            this.deleteCookie(key);
            this.deleteCookie(metaRecordKey);
            return null;
        }
        const expiresAt = meta.expiresAt;
        if (expiresAt && expiresAt.getTime() <= Date.now()) {
            // expired. No need to clean up as the browser will do it for us
            return null;
        }
        const data = this.handler.getCookie(key);
        if (!data) {
            return null;
        }
        return { data, meta };
    }
    getLSRecord(key, metaRecordKey) {
        const metaRecord = this.handler.getDataFromLocalStorage(metaRecordKey);
        if (!metaRecord || metaRecord.length === 0) {
            return null;
        }
        let meta;
        try {
            meta = this.parseMetaRecord(metaRecord);
        }
        catch (e) {
            this.eventBus.emitErrorWithMessage('Cache', 'Failed reading meta from ls', e);
            this.handler.removeDataFromLocalStorage(key);
            this.handler.removeDataFromLocalStorage(metaRecordKey);
            return null;
        }
        const expiresAt = meta.expiresAt;
        if (expiresAt && expiresAt.getTime() <= Date.now()) {
            // expired.
            this.handler.removeDataFromLocalStorage(key);
            this.handler.removeDataFromLocalStorage(metaRecordKey);
            return null;
        }
        const data = this.handler.getDataFromLocalStorage(key);
        if (!data) {
            return null;
        }
        return { data, meta };
    }
    get(key) {
        const metaRecordKey = metaKey(key);
        const cookieRecord = this.getCookieRecord(key, metaRecordKey);
        const lsRecord = this.getLSRecord(key, metaRecordKey);
        if (cookieRecord && lsRecord) {
            // comparing dates with getTime() because Date objects are not equal
            if (cookieRecord.meta.writtenAt.getTime() === lsRecord.meta.writtenAt.getTime()) {
                return cookieRecord;
            }
            else if (cookieRecord.meta.writtenAt > lsRecord.meta.writtenAt) {
                // cookie record is newer. Update ls record
                this.handler.setDataInLocalStorage(key, cookieRecord.data);
                this.handler.setDataInLocalStorage(metaRecordKey, this.serializeMetaRecord(cookieRecord.meta));
                return cookieRecord;
            }
            else {
                // ls record is newer. Update cookie record
                this.handler.setCookie(key, lsRecord.data, lsRecord.meta.expiresAt, 'Lax', this.cookieDomain);
                this.handler.setCookie(metaRecordKey, this.serializeMetaRecord(lsRecord.meta), lsRecord.meta.expiresAt, 'Lax', this.cookieDomain);
                return lsRecord;
            }
        }
        else if (cookieRecord) {
            // only cookie record exists. Write to ls
            this.handler.setDataInLocalStorage(key, cookieRecord.data);
            this.handler.setDataInLocalStorage(metaRecordKey, this.serializeMetaRecord(cookieRecord.meta));
            return cookieRecord;
        }
        else if (lsRecord) {
            // only ls record exists. Write to cookie
            this.handler.setCookie(key, lsRecord.data, lsRecord.meta.expiresAt, 'Lax', this.cookieDomain);
            this.handler.setCookie(metaRecordKey, this.serializeMetaRecord(lsRecord.meta), lsRecord.meta.expiresAt, 'Lax', this.cookieDomain);
            return lsRecord;
        }
        else {
            return null;
        }
    }
    set(key, value, expires) {
        const metaRecordKey = metaKey(key);
        const metaRecord = this.serializeMetaRecord({ writtenAt: new Date(), expiresAt: expires });
        // set in ls
        this.handler.setDataInLocalStorage(key, value);
        this.handler.setDataInLocalStorage(metaRecordKey, metaRecord);
        // needs to be set as cookies will be session cookies otherwise. Try to make them live as long as possible
        const cookieExpires = expires || expiresInDays(DEFAULT_COOKIE_EXPIRATION_DAYS);
        // set in cookie
        this.handler.setCookie(key, value, cookieExpires, 'Lax', this.cookieDomain);
        this.handler.setCookie(metaRecordKey, metaRecord, cookieExpires, 'Lax', this.cookieDomain);
    }
}
function metaKey(baseKey) {
    return `${baseKey}_meta`;
}

const empty = () => undefined;
function privacyCheck(wrapper, privacyMode, functionName) {
    return (privacyMode) ? empty : wrapper.wrap(functionName);
}
class WrappedCallHandler {
    functions;
    constructor(externalCallHandler, eventBus, privacyMode) {
        const wrapper = new WrappingContext(externalCallHandler, 'CallHandler', eventBus);
        this.functions = {
            ajaxGet: privacyCheck(wrapper, privacyMode, 'ajaxGet'),
            pixelGet: privacyCheck(wrapper, privacyMode, 'pixelGet')
        };
        wrapper.reportErrors();
    }
    ajaxGet(url, onSuccess, onError, timeout, headers) {
        this.functions.ajaxGet(url, onSuccess, onError, timeout, headers);
    }
    pixelGet(url, onLoad) {
        this.functions.pixelGet(url, onLoad);
    }
}

function enrichIdCookie(storageHandler) {
    return state => {
        if (state.idCookie?.strategy === 'cookie' && typeof state.idCookie?.name === 'string') {
            return { ...state, resolvedIdCookie: storageHandler.getCookie(state.idCookie.name) };
        }
        else if (state.idCookie?.strategy === 'localStorage' && typeof state.idCookie?.name === 'string') {
            return { ...state, resolvedIdCookie: storageHandler.getDataFromLocalStorage(state.idCookie.name) };
        }
        else {
            return state;
        }
    };
}

const hemStore = {};
function pushSingleEvent(event, pixelClient, enrichedState, eventBus) {
    if (!event || !isObject(event)) {
        // @ts-ignore
        eventBus.emitErrorWithMessage('EventNotAnObject', 'Received event was not an object', new Error(event));
    }
    else if ('config' in event) {
        eventBus.emitErrorWithMessage('StrayConfig', 'Received a config after LC has already been initialised', new Error(JSON.stringify(event)));
    }
    else {
        // const stateWithEventSource: StateWithEventSource = { eventSource: event, ...enrichedState }
        const wrapper = StateWrapper.fromEvent(enrichedState, event, eventBus);
        // const combined = wrapper.combineWith({ eventSource: event })
        if (wrapper.getHashedEmail().length > 0) {
            hemStore.hashedEmail = wrapper.getHashedEmail();
        }
        else if (hemStore.hashedEmail) {
            wrapper.setHashedEmail(hemStore.hashedEmail);
        }
        const onPreSend = () => eventBus.emit(PRELOAD_PIXEL, '0');
        const onLoad = () => eventBus.emit(PIXEL_SENT_PREFIX, enrichedState);
        pixelClient.sendAjax(wrapper, { onPreSend, onLoad });
    }
}
function configMatcher(previousConfig, newConfig) {
    const equalConfigs = previousConfig.appId === newConfig.appId &&
        previousConfig.wrapperName === newConfig.wrapperName &&
        previousConfig.collectorUrl === newConfig.collectorUrl;
    if (!equalConfigs) {
        return {
            appId: [previousConfig.appId, newConfig.appId],
            wrapperName: [previousConfig.wrapperName, newConfig.wrapperName],
            collectorUrl: [previousConfig.collectorUrl, newConfig.collectorUrl]
        };
    }
}
function processArgs(args, pixelClient, enrichedState, eventBus) {
    try {
        args.forEach(arg => {
            const event = arg;
            if (isArray(event)) {
                event.forEach(e => pushSingleEvent(e, pixelClient, enrichedState, eventBus));
            }
            else {
                pushSingleEvent(event, pixelClient, enrichedState, eventBus);
            }
        });
    }
    catch (e) {
        console.error('Error sending events', e);
        eventBus.emitErrorWithMessage('LCPush', 'Failed sending an event', e);
    }
}
function getInitializedLiveConnect(liveConnectConfig) {
    try {
        // @ts-ignore
        if (window && window[liveConnectConfig.globalVarName] && window[liveConnectConfig.globalVarName].ready) {
            // @ts-ignore
            const mismatchedConfig = window[liveConnectConfig.globalVarName].config && configMatcher(window[liveConnectConfig.globalVarName].config, liveConnectConfig);
            if (mismatchedConfig) {
                const error = new Error();
                error.name = 'ConfigSent';
                error.message = 'Additional configuration received';
                // @ts-ignore
                const eventBus = getAvailableBus(liveConnectConfig.globalVarName);
                // @ts-ignore
                window[liveConnectConfig.globalVarName].eventBus = eventBus;
                eventBus.emitErrorWithMessage('LCDuplication', JSON.stringify(mismatchedConfig), error);
            }
            // @ts-ignore
            return window[liveConnectConfig.globalVarName];
        }
    }
    catch (e) {
        console.error('Could not initialize error bus');
    }
}
// @ts-ignore
function standardInitialization(liveConnectConfig, externalStorageHandler, externalCallHandler, eventBus) {
    try {
        // TODO: proper config validation
        const validLiveConnectConfig = {
            ...removeInvalidPairs(liveConnectConfig, eventBus),
            identifiersToResolve: liveConnectConfig.identifiersToResolve || [],
            contextSelectors: liveConnectConfig.contextSelectors || '',
            contextElementsLength: liveConnectConfig.contextElementsLength || 0
        };
        const stateWithStorage = enrichPage(enrichStorageStrategy(enrichPrivacyMode(validLiveConnectConfig)));
        const callHandler = new WrappedCallHandler(externalCallHandler, eventBus, stateWithStorage.privacyMode);
        const storageHandler = WrappedStorageHandler.make(stateWithStorage.storageStrategy, externalStorageHandler, eventBus);
        const stateWithDomain = enrichDomain(storageHandler)(stateWithStorage);
        const cache = new StorageHandlerBackedCache({
            storageHandler,
            eventBus,
            cookieDomain: stateWithDomain.cookieDomain
        });
        const enrichedState = enrichIdCookie(storageHandler)(enrichLiveConnectId(cache, storageHandler)(enrichDecisionIds(storageHandler, eventBus)(enrichIdentifiers$1(storageHandler, eventBus)(stateWithDomain))));
        const pixelSender = new PixelSender({
            collectorUrl: validLiveConnectConfig.collectorUrl,
            ajaxTimeout: validLiveConnectConfig.ajaxTimeout,
            eventBus,
            callHandler
        });
        register(enrichedState, pixelSender, eventBus);
        const resolver = new IdentityResolver(enrichedState, callHandler, eventBus);
        const _push = (...args) => processArgs(args, pixelSender, enrichedState, eventBus);
        return {
            push: _push,
            fire: () => _push({}),
            peopleVerifiedId: enrichedState.peopleVerifiedId,
            ready: true,
            resolve: resolver.resolve.bind(resolver),
            resolutionCallUrl: resolver.getUrl.bind(resolver),
            config: validLiveConnectConfig,
            eventBus,
            storageHandler,
            cache
        };
    }
    catch (x) {
        console.error(x);
        eventBus.emitErrorWithMessage('LCConstruction', 'Failed to build LC', x);
    }
}
function initializeWithoutGlobalName$1(liveConnectConfig, externalStorageHandler, externalCallHandler, eventBus) {
    const lc = standardInitialization(liveConnectConfig, externalStorageHandler, externalCallHandler, eventBus);
    window.liQ_instances = window.liQ_instances || [];
    window.liQ_instances.push(lc);
    return lc;
}
function initializeWithGlobalName$1(liveConnectConfig, externalStorageHandler, externalCallHandler, eventBus) {
    // @ts-ignore
    const queue = window[liveConnectConfig.globalVarName] || [];
    const lc = getInitializedLiveConnect(liveConnectConfig) || standardInitialization(liveConnectConfig, externalStorageHandler, externalCallHandler, eventBus) || queue;
    if (isArray(queue)) {
        for (let i = 0; i < queue.length; i++) {
            lc.push(queue[i]);
        }
    }
    // @ts-ignore
    window[lc.config.globalVarName] = lc;
    window.liQ_instances = window.liQ_instances || [];
    // @ts-ignore
    if (window.liQ_instances.filter(i => i.config.globalVarName === lc.config.globalVarName).length === 0) {
        window.liQ_instances.push(lc);
    }
    return lc;
}
function StandardLiveConnect(liveConnectConfig, externalStorageHandler, externalCallHandler, externalEventBus) {
    const configuration = (isObject(liveConnectConfig) && liveConnectConfig) || {};
    const eventBus = externalEventBus || LocalEventBus();
    let lc;
    try {
        lc = configuration.globalVarName
            ? initializeWithGlobalName$1(configuration, externalStorageHandler, externalCallHandler, eventBus)
            : initializeWithoutGlobalName$1(configuration, externalStorageHandler, externalCallHandler, eventBus);
    }
    catch (e) {
        console.error(e);
        eventBus.emitErrorWithMessage('LCConstruction', 'Failed to build LC', e);
    }
    // @ts-ignore
    return lc;
}

function enrichPeopleVerifiedId(storageHandler, eventBus) {
    return state => {
        try {
            return {
                ...state,
                peopleVerifiedId: state.peopleVerifiedId || storageHandler.getDataFromLocalStorage(PEOPLE_VERIFIED_LS_ENTRY) || undefined
            };
        }
        catch (e) {
            eventBus.emitError('PeopleVerifiedEnrich', e);
            return state;
        }
    };
}

function enrichIdentifiers(storageHandler, eventBus) {
    return state => {
        try {
            return { ...state, retrievedIdentifiers: resolveIdentifiers(state.identifiersToResolve, storageHandler) };
        }
        catch (e) {
            eventBus.emitError('IdentifiersEnrich', e);
            return { ...state, retrievedIdentifiers: [] };
        }
    };
}
function resolveIdentifiers(identifiersToResolve, storageHandler) {
    const cookieNames = isArray(identifiersToResolve) ? identifiersToResolve : safeToString(identifiersToResolve).split(',');
    const identifiers = [];
    for (let i = 0; i < cookieNames.length; i++) {
        const identifierName = trim(cookieNames[i]);
        const identifierValue = storageHandler.getCookie(identifierName) || storageHandler.getDataFromLocalStorage(identifierName);
        if (identifierValue && !containsEmailField(safeToString(identifierValue)) && !isEmail(safeToString(identifierValue))) {
            identifiers.push({
                name: identifierName,
                value: safeToString(identifierValue)
            });
        }
    }
    return identifiers;
}

// @ts-ignore
function _minimalInitialization(liveConnectConfig, externalStorageHandler, externalCallHandler, eventBus, push) {
    try {
        const validLiveConnectConfig = {
            ...removeInvalidPairs(liveConnectConfig, eventBus),
            identifiersToResolve: liveConnectConfig.identifiersToResolve || []
        };
        const stateWithStorage = enrichStorageStrategy(enrichPrivacyMode(validLiveConnectConfig));
        const storageHandler = WrappedReadOnlyStorageHandler.make(stateWithStorage.storageStrategy, externalStorageHandler, eventBus);
        const callHandler = new WrappedCallHandler(externalCallHandler, eventBus, stateWithStorage.privacyMode);
        const enrichedState = enrichIdentifiers(storageHandler, eventBus)(enrichIdCookie(storageHandler)(enrichPeopleVerifiedId(storageHandler, eventBus)(stateWithStorage)));
        const resolver = new IdentityResolver(enrichedState, callHandler, eventBus);
        return {
            push: (arg) => push(arg),
            fire: () => push({}),
            peopleVerifiedId: enrichedState.peopleVerifiedId,
            ready: true,
            resolve: resolver.resolve.bind(resolver),
            resolutionCallUrl: resolver.getUrl.bind(resolver),
            config: validLiveConnectConfig,
            eventBus,
            // @ts-ignore
            storageHandler
        };
    }
    catch (x) {
        console.error(x);
    }
}
function initializeWithoutGlobalName(liveConnectConfig, externalStorageHandler, externalCallHandler, eventBus) {
    const lc = _minimalInitialization(liveConnectConfig, externalStorageHandler, externalCallHandler, eventBus, () => { });
    window.liQ_instances = window.liQ_instances || [];
    window.liQ_instances.push(lc);
    return lc;
}
function initializeWithGlobalName(liveConnectConfig, externalStorageHandler, externalCallHandler, eventBus) {
    // @ts-ignore
    const queue = window[liveConnectConfig.globalVarName] = window[liveConnectConfig.globalVarName] || [];
    const push = queue.push.bind(queue);
    const lc = _minimalInitialization(liveConnectConfig, externalStorageHandler, externalCallHandler, eventBus, push);
    window.liQ_instances = window.liQ_instances || [];
    // @ts-ignore
    if (window.liQ_instances.filter(i => i.config.globalVarName === lc.config.globalVarName).length === 0) {
        window.liQ_instances.push(lc);
    }
    return lc;
}
function MinimalLiveConnect(liveConnectConfig, externalStorageHandler, externalCallHandler, externalEventBus) {
    const configuration = (isObject(liveConnectConfig) && liveConnectConfig) || {};
    const eventBus = externalEventBus || LocalEventBus();
    try {
        return configuration.globalVarName
            ? initializeWithGlobalName(configuration, externalStorageHandler, externalCallHandler, eventBus)
            : initializeWithoutGlobalName(configuration, externalStorageHandler, externalCallHandler, eventBus);
    }
    catch (x) {
        console.error(x);
    }
    // @ts-ignore
    return {};
}

function LiveConnect(liveConnectConfig, externalStorageHandler, externalCallHandler, mode, externalEventBus) {
    const minimalMode = mode === 'minimal';
    const bus = externalEventBus || LocalEventBus();
    const configuration = (isObject(liveConnectConfig) && liveConnectConfig) || {};
    const initializationFunction = minimalMode ? MinimalLiveConnect : StandardLiveConnect;
    return initializationFunction(configuration, externalStorageHandler, externalCallHandler, bus);
}

export { LiveConnect as L, MinimalLiveConnect as M, QueryBuilder as Q, StorageHandlerBackedCache as S, WrappedStorageHandler as W, StandardLiveConnect as a, eventBus as b, consts as c, encodeIdCookie as e };
